import { useCallback, useMemo } from "react";
import { useAuthStore } from "../stores/auth.store";
import { useOrganizationStore } from "../stores/organization.store";
import { OrganizationType } from "../types/organization.type";
import {
  canAccessOrg,
  getSitesForOrg,
  resolveEsgNavPermission,
} from "../auth";
import type { EsgNavItem } from "../auth";
import { isReadOnlyAdmin } from "../utils/auth.helpers";

export function useAuthPermissions() {
  const {
    tokenPayload,
    permissionList,
    isPlatformAdmin: platformAdmin,
    user,
    canAccessPermission,
  } = useAuthStore();
  const { selectedOrganization } = useOrganizationStore();

  const orgId = selectedOrganization?.id ?? null;
  const readOnly = useMemo(
    () => isReadOnlyAdmin(user, tokenPayload),
    [user, tokenPayload],
  );

  const can = useCallback(
    (required: string, legacyRoles?: readonly string[]) =>
      canAccessPermission(required, legacyRoles, orgId),
    [canAccessPermission, orgId],
  );

  const canMutateAction = useCallback(
    (required: string, legacyRoles?: readonly string[]) =>
      !readOnly && can(required, legacyRoles),
    [readOnly, can],
  );

  const canSeeEsgNavItem = useCallback(
    (item: EsgNavItem): boolean => {
      if (platformAdmin) {
        return true;
      }

      const perm = resolveEsgNavPermission(item.entity, item.action, orgId);
      if (!perm) {
        return false;
      }
      return can(perm, item.legacyRoles);
    },
    [platformAdmin, orgId, can],
  );

  const filterEsgNavItems = useCallback(
    (items: EsgNavItem[], orgType: OrganizationType): EsgNavItem[] => {
      return items
        .filter((item) => !item.hidden)
        .filter((item) => item.allowedTypes.includes(orgType))
        .filter((item) => canSeeEsgNavItem(item));
    },
    [canSeeEsgNavItem],
  );

  const canAccessSelectedOrg = useCallback(
    () => canAccessOrg(tokenPayload, permissionList, orgId),
    [tokenPayload, permissionList, orgId],
  );

  const sitesForOrg = useCallback(
    (targetOrgId: string) => getSitesForOrg(tokenPayload, targetOrgId),
    [tokenPayload],
  );

  return {
    orgId,
    user,
    tokenPayload,
    permissionList,
    isPlatformAdmin: platformAdmin,
    isReadOnly: readOnly,
    can,
    canMutate: canMutateAction,
    canSeeEsgNavItem,
    filterEsgNavItems,
    canAccessSelectedOrg,
    sitesForOrg,
  };
}
